import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Divider,
  LinearProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  Search,
  FiberManualRecord,
  TrafficRounded,
  People,
  Category,
  Campaign,
  Refresh,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  PersonAdd,
} from "@mui/icons-material";
import { getRequest, server } from "../config";
import { buildCityCategoryCoverage } from "./cityTrafficSignalUtils";

const PARTNER_THRESHOLD = 25;
const GREEN_THRESHOLD = 90;
const YELLOW_THRESHOLD = 50;

const SIGNAL_CONFIG = {
  green: {
    label: "Green City",
    color: "#2e7d32",
    bgColor: "#e8f5e9",
    borderColor: "#4caf50",
    icon: <CheckCircle sx={{ color: "#2e7d32" }} />,
    description: `≥${GREEN_THRESHOLD}% categories have ${PARTNER_THRESHOLD}+ partners. Focus on Customer Ads.`,
    adStrategy: "🎯 Run Customer Advertisements",
    chipColor: "success",
  },
  yellow: {
    label: "Yellow City",
    color: "#e65100",
    bgColor: "#fff8e1",
    borderColor: "#ffc107",
    icon: <Warning sx={{ color: "#f57f17" }} />,
    description: `${YELLOW_THRESHOLD}–${GREEN_THRESHOLD - 1}% categories have ${PARTNER_THRESHOLD}+ partners. Partner + Manager Ads + Some Customer Ads.`,
    adStrategy: "📢 Partner & Manager Ads + Limited Customer Ads",
    chipColor: "warning",
  },
  red: {
    label: "Red City",
    color: "#b71c1c",
    bgColor: "#ffebee",
    borderColor: "#f44336",
    icon: <ErrorIcon sx={{ color: "#c62828" }} />,
    description: `<${YELLOW_THRESHOLD}% categories have ${PARTNER_THRESHOLD}+ partners. Focus on Manager + Partner Ads only.`,
    adStrategy: "📣 Manager & Partner Recruitment Ads Only",
    chipColor: "error",
  },
};

// ─── Legend ──────────────────────────────────────────────────────────────────
const LegendCard = () => (
  <Paper sx={{ p: 3, mb: 3, background: "linear-gradient(135deg, #f5f7fa 0%, #e8eaf6 100%)" }}>
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      <TrafficRounded color="primary" />
      <Typography variant="h6" fontWeight="bold">
        Traffic Signal System — How it Works
      </Typography>
    </Box>
    <Grid container spacing={2}>
      {Object.entries(SIGNAL_CONFIG).map(([key, cfg]) => (
        <Grid item xs={12} md={4} key={key}>
          <Box sx={{ p: 2, borderRadius: 2, backgroundColor: cfg.bgColor, border: `2px solid ${cfg.borderColor}` }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {cfg.icon}
              <Typography fontWeight="bold" sx={{ color: cfg.color }}>{cfg.label}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1}>{cfg.description}</Typography>
            <Typography variant="caption" fontWeight="medium" sx={{ color: cfg.color }}>{cfg.adStrategy}</Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
    <Box mt={2}>
      <Typography variant="caption" color="text.secondary">
        * Signal = (Categories with ≥{PARTNER_THRESHOLD} verified partners) ÷ (Total categories) × 100
      </Typography>
    </Box>
  </Paper>
);

// ─── Fetch partner count for a specific category in a city ───────────────────
const fetchPartnerCountForCategory = async (cityName, categoryId) => {
  try {
    const res = await getRequest(
      `${server.uri}users/count?where={"userType":"partner","city":"${cityName}","status":"verified","categoryId":"${categoryId}"}`
    );
    return res?.count || 0;
  } catch (err) {
    return 0;
  }
};

// ─── City Card ────────────────────────────────────────────────────────────────
const CityTrafficCard = ({ city, totalCategories, allCategories, onSignalReady }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city.name, totalCategories, allCategories]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const cityName = city.name;

      // 1. Use the same count query as PartnerList for the city headline total.
      const [totalRes, partnersRes] = await Promise.all([
        getRequest(`${server.uri}users/count?where={"userType":"partner","city":"${cityName}"}`),
        getRequest(
          `${server.uri}users?filter={"where":{"userType":"partner","city":"${cityName}"},"limit":5000,"include":[{"relation":"services"}]}`
        ),
      ]);
      const partners = Array.isArray(partnersRes) ? partnersRes : partnersRes?.users || [];
      const verifiedPartners = partners.filter((partner) => partner.status === "verified");
      const totalPartners = totalRes?.count ?? partners.length;

      // 2. Signal coverage is intentionally based on verified partners only.
      const { categoryDetails, coveredCategories, coveragePercent } = buildCityCategoryCoverage(verifiedPartners, allCategories);
      const categoriesMeetingThreshold = coveredCategories;

      // 3. Compute signal
      const signal = coveragePercent >= GREEN_THRESHOLD ? "green" : coveragePercent >= YELLOW_THRESHOLD ? "yellow" : "red";

      // 4. Derive stats from partners list
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      setData({
        totalPartners,
        activePartners: verifiedPartners.filter((p) => p.isUserActive === true).length,
        newPartnersMonth: verifiedPartners.filter((p) => p.createdAt && new Date(p.createdAt) >= thirtyDaysAgo).length,
        coveredCategories: categoriesMeetingThreshold,
        totalCategories,
        coveragePercent,
        signal,
        categoryDetails,
      });
      onSignalReady(city.cityId, signal);
    } catch (err) {
      console.error("Error fetching city traffic data:", err);
      setData({ totalPartners: 0, activePartners: 0, newPartnersMonth: 0, coveredCategories: 0, totalCategories, coveragePercent: 0, signal: "red", categoryDetails: [] });
      onSignalReady(city.cityId, "red");
    } finally {
      setLoading(false);
    }
  };

  const cfg = data ? SIGNAL_CONFIG[data.signal] : SIGNAL_CONFIG.red;

  return (
    <>
      <Card sx={{ border: `2px solid ${cfg.borderColor}`, borderRadius: 3, height: "100%", transition: "box-shadow 0.2s", "&:hover": { boxShadow: 6 } }}>
        <CardHeader
          avatar={
            <Avatar sx={{ backgroundColor: cfg.bgColor, border: `2px solid ${cfg.borderColor}` }}>
              <FiberManualRecord
                sx={{
                  color: data?.signal === "green" ? "#2e7d32" : data?.signal === "yellow" ? "#f57f17" : "#c62828",
                  fontSize: 28,
                }}
              />
            </Avatar>
          }
          title={<Typography variant="h6" fontWeight="bold">{city.name}</Typography>}
          subheader={
            loading ? <CircularProgress size={14} /> : (
              <Chip label={cfg.label} size="small" color={cfg.chipColor} sx={{ mt: 0.5, fontWeight: "bold" }} />
            )
          }
          action={
            <Button size="small" variant="outlined" onClick={() => setDetailOpen(true)} disabled={loading} sx={{ mt: 1, mr: 1 }}>
              Details
            </Button>
          }
        />
        <Divider />
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
          ) : (
            <>
              {/* Coverage Bar */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">Categories with ≥{PARTNER_THRESHOLD} Partners</Typography>
                  <Typography variant="caption" fontWeight="bold" sx={{ color: cfg.color }}>
                    {data.coveredCategories}/{data.totalCategories} ({data.coveragePercent}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(data.coveragePercent, 100)}
                  sx={{
                    height: 10, borderRadius: 5, backgroundColor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": { backgroundColor: cfg.borderColor, borderRadius: 5 },
                  }}
                />
              </Box>

              {/* Stats */}
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Box sx={{ backgroundColor: "#e3f2fd", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                    <Typography variant="h6" fontWeight="bold" color="primary">{data.activePartners}</Typography>
                    <Typography variant="caption" color="text.secondary">Active Now</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ backgroundColor: "#f3e5f5", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: "#7b1fa2" }}>{data.totalPartners}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Partners</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ backgroundColor: "#e8f5e9", borderRadius: 2, p: 1.5, textAlign: "center" }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: "#2e7d32" }}>{data.newPartnersMonth}</Typography>
                    <Typography variant="caption" color="text.secondary">New (30d)</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Ad Strategy */}
              <Box mt={2} sx={{ backgroundColor: cfg.bgColor, borderRadius: 2, p: 1.5, border: `1px dashed ${cfg.borderColor}` }}>
                <Typography variant="caption" fontWeight="bold" sx={{ color: cfg.color }}>{cfg.adStrategy}</Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FiberManualRecord sx={{ color: data?.signal === "green" ? "#2e7d32" : data?.signal === "yellow" ? "#f57f17" : "#c62828" }} />
            {city.name} — {cfg.label}
          </Box>
        </DialogTitle>
        <DialogContent>
          {data && (
            <>
              <Alert severity={data.signal === "green" ? "success" : data.signal === "yellow" ? "warning" : "error"} sx={{ mb: 2 }}>
                <AlertTitle>{cfg.label}</AlertTitle>
                {cfg.description}
              </Alert>
              <List dense>
                  <ListItem>
                    <ListItemIcon><People color="primary" /></ListItemIcon>
                    <ListItemText primary="Active Partners Right Now" secondary={data.activePartners} />
                  </ListItem>
                <ListItem>
                  <ListItemIcon><People color="secondary" /></ListItemIcon>
                  <ListItemText primary="Total Partners in City" secondary={data.totalPartners} />
                </ListItem>
                <ListItem>
                    <ListItemIcon><PersonAdd color="success" /></ListItemIcon>
                    <ListItemText primary="New Partners (Last 30 Days)" secondary={data.newPartnersMonth} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Category color="secondary" /></ListItemIcon>
                  <ListItemText
                    primary={`Categories with ≥${PARTNER_THRESHOLD} Partners`}
                    secondary={`${data.coveredCategories} of ${data.totalCategories} categories (${data.coveragePercent}%)`}
                  />
                </ListItem>
              </List>

              {/* Category-wise breakdown */}
              {data.categoryDetails && data.categoryDetails.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Category-wise Partner Count (Threshold: ≥{PARTNER_THRESHOLD})
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                    <List dense>
                      {data.categoryDetails.map((cat) => (
                        <ListItem key={cat.categoryId} sx={{ py: 0.3 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            {cat.meetsThreshold ? (
                              <CheckCircle sx={{ color: "#2e7d32", fontSize: 18 }} />
                            ) : (
                              <Warning sx={{ color: "#e65100", fontSize: 18 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={cat.categoryName}
                            secondary={`${cat.partnerCount} partner${cat.partnerCount !== 1 ? "s" : ""} ${
                              cat.meetsThreshold ? "✅" : `❌ (need ${PARTNER_THRESHOLD - cat.partnerCount} more)`
                            }`}
                            primaryTypographyProps={{ variant: "body2" }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </>
              )}

              <Box mt={2} sx={{ backgroundColor: cfg.bgColor, borderRadius: 2, p: 1.5, border: `1px dashed ${cfg.borderColor}` }}>
                <Typography variant="caption" fontWeight="bold" sx={{ color: cfg.color }}>
                  <Campaign sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                  {cfg.adStrategy}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const CityTrafficSignal = () => {
  const [cities, setCities] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [totalCategories, setTotalCategories] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterSignal, setFilterSignal] = useState("all");
  // Map of cityId → signal ("green"/"yellow"/"red") reported by cards
  const [signalMap, setSignalMap] = useState({});

  useEffect(() => { init(); }, []);

  const init = async () => {
    setLoading(true);
    setSignalMap({});
    const [citiesRes, categoriesRes] = await Promise.all([
      getRequest(`${server.uri}cities`),
      getRequest(`${server.uri}categories`),
    ]);
    if (citiesRes) setCities(citiesRes);
    if (Array.isArray(categoriesRes)) {
      // Deduplicate by categoryId
      const unique = categoriesRes.filter((c, i, arr) => arr.findIndex((x) => x.categoryId === c.categoryId) === i);
      setAllCategories(unique);
      setTotalCategories(unique.length);
    } else if (categoriesRes?.count) {
      setTotalCategories(categoriesRes.count);
    }
    setLoading(false);
  };

  // Called by each card once it knows its signal
  const handleSignalReady = (cityId, signal) => {
    setSignalMap((prev) => ({ ...prev, [cityId]: signal }));
  };

  // Apply search + signal filter
  const filteredCities = cities.filter((city) => {
    const matchesSearch = city.name.toLowerCase().includes(search.toLowerCase());
    const citySignal = signalMap[city.cityId];
    const matchesSignal =
      filterSignal === "all" ||
      citySignal === filterSignal ||
      // Show cities whose signal isn't known yet only in "all" mode
      citySignal === undefined;
    return matchesSearch && matchesSignal;
  });

  // Count per signal for summary chips
  const counts = { green: 0, yellow: 0, red: 0 };
  Object.values(signalMap).forEach((s) => { if (counts[s] !== undefined) counts[s]++; });

  const filterButtons = [
    { key: "all",    label: `All Cities (${cities.length})`,  color: "default"  },
    { key: "green",  label: `🟢 Green (${counts.green})`,     color: "success"  },
    { key: "yellow", label: `🟡 Yellow (${counts.yellow})`,   color: "warning"  },
    { key: "red",    label: `🔴 Red (${counts.red})`,         color: "error"    },
  ];

  return (
    <Box sx={{ p: 3, mt: 6 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="h4" fontWeight="bold">City Traffic Signal System</Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor city readiness — Green when every category has ≥{PARTNER_THRESHOLD} verified partners
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <Button variant="outlined" startIcon={<Refresh />} onClick={init}>Refresh</Button>
        </Tooltip>
      </Box>

      <LegendCard />

      {/* Search + Filter */}
      <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
        <TextField
          variant="outlined"
          placeholder="Search city..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
          }}
        />
        {filterButtons.map((btn) => (
          <Chip
            key={btn.key}
            label={btn.label}
            color={btn.color}
            variant={filterSignal === btn.key ? "filled" : "outlined"}
            clickable
            onClick={() => setFilterSignal(btn.key)}
            sx={{ fontWeight: "bold" }}
          />
        ))}
      </Box>

      {/* City Cards */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : filteredCities.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No cities found for this filter.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredCities.map((city) => (
            <Grid item xs={12} sm={6} md={4} key={city.cityId}>
              <CityTrafficCard
                city={city}
                totalCategories={totalCategories}
                allCategories={allCategories}
                onSignalReady={handleSignalReady}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CityTrafficSignal;